import { WebSocketAPI, Share, FileElement } from "@xlabmedical/medic-api";
import { devLogger } from "@xlabmedical/medic-web-framework";

export interface WebRgWSEventData {
    id: number;
    uid: string;
    lastModified: string; // ISO 8601 timestamp, e.g. "2025-06-04T12:24:27Z"
    step: string;
    status: string;
    stepId: number;
    statusId: number;
    projectName: string;
    allowsUserEditing: boolean;
    productionCenterId: number;
    shares?: Share[];
    files?: FileElement[];
    originProjectReference?: string;
    statusData?: string;
}

export interface WebRgWSEvent {
    type: string;
    projectId: number;
    projectName: string;
    projectFileId: number;
}

export interface WebRgWSChatEvent extends WebRgWSEvent {
    type: "new-chat-message";
    data: WebRgWSEventData;
    patientId: number;
}

export interface WebRgWSWeblinkOrderCreatedEvent extends WebRgWSEvent {
    type: "WEBLINK_ORDER_CREATED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSWeblinkOrderUpdatedEvent extends WebRgWSEvent {
    type: "WEBLINK_ORDER_UPDATED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSProjectFileAddedEvent extends WebRgWSEvent {
    type: "PROJECT_FILE_ADDED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSProjectFileUpdatedEvent extends WebRgWSEvent {
    type: "PROJECT_FILE_UPDATED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSProjectFileDeletedEvent extends WebRgWSEvent {
    type: "PROJECT_FILE_DELETED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSUnzippedProjectFilesUpdatedEvent extends WebRgWSEvent {
    type: "UNZIPPED_PROJECT_FILES_UPDATED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSProjectUpdatedEvent extends WebRgWSEvent {
    type: "PROJECT_UPDATED";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWSProjectAddedEvent {
    type: "PROJECT_ADDED";
    data?: Partial<WebRgWSEventData> & {
        name?: string;
        projectName?: string;
    };
    projectId?: number;
    projectName?: string;
    projectFileId?: number;
}

export interface WebRgWSJobStatusUpdatedEvent extends WebRgWSEvent {
    type: "JOB_STATUS_UPDATE";
    data: WebRgWSEventData;
    projectName: string;
}

export interface WebRgWebsocketEvents {
    chatMessageEvent: (event: WebRgWSChatEvent) => void;
    orderCreatedEvent: (event: WebRgWSWeblinkOrderCreatedEvent) => void;
    orderUpdatedEvent: (event: WebRgWSWeblinkOrderUpdatedEvent) => void;
    projectFileAddedEvent: (event: WebRgWSProjectFileAddedEvent) => void;
    projectFileUpdatedEvent: (event: WebRgWSProjectFileUpdatedEvent) => void;
    projectFileDeletedEvent: (event: WebRgWSProjectFileDeletedEvent) => void;
    projectUpdatedEvent: (event: WebRgWSProjectUpdatedEvent) => void;
    projectAddedEvent: (event: WebRgWSProjectAddedEvent) => void;
    unzippedProjectFilesUpdatedEvent: (event: WebRgWSUnzippedProjectFilesUpdatedEvent) => void;
    jobStatusUpdatedEvent: (event: WebRgWSJobStatusUpdatedEvent) => void;
}

export class WebRgWebsocketManager {
    private ws?: WebSocketAPI;
    private baseWsUrl: string;

    private eventHandlers: WebRgWebsocketEvents[] = [];
    constructor(baseWsUrl: string) {
        this.baseWsUrl = baseWsUrl;
    }

    public connect(sessionId?: string) {
        this.eventHandlers = [];
        const wsUrl = this.buildWsUrl(sessionId);
        devLogger.debug("WebRgWebsocketManager: connecting", {
            wsUrl,
            hasSessionId: !!sessionId,
        });
        this.ws = new WebSocketAPI(wsUrl, {
            reconnectDelaysMs: [],
            reconnectOnUnauthorized: false,
        });
        this.ws.addEventHandler((event) => {
            this.onMessage(event);
        });
        this.ws.openConnection();
    }

    public disconnect() {
        this.eventHandlers = [];
        if (this.ws) {
            devLogger.debug("WeblinkWebsocketManager: disconnecting...");
            this.ws?.removeEventHandler((event) => {
                this.onMessage(event);
            });
            this.ws?.closeConnection();
        }
    }

    public addEventHandler(handler: WebRgWebsocketEvents) {
        this.eventHandlers.push(handler);
    }

    public removeEventHandler(handler: WebRgWebsocketEvents) {
        this.eventHandlers = this.eventHandlers.filter((h) => h != handler);
    }

    private onMessage(event: MessageEvent) {
        const parsedData = JSON.parse(event.data);
        devLogger.debug("WebRg useWebsocket onMessage:", parsedData);

        if (parsedData.type == "NEW_CHAT_MESSAGE") {
            this.eventHandlers.forEach((handler) => {
                handler.chatMessageEvent(parsedData);
            });
        }

        if (parsedData.type == "WEBLINK_ORDER_CREATED") {
            this.eventHandlers.forEach((handler) => {
                handler.orderCreatedEvent(parsedData);
            });
        }

        if (parsedData.type == "WEBLINK_ORDER_UPDATED") {
            this.eventHandlers.forEach((handler) => {
                handler.orderUpdatedEvent(parsedData);
            });
        }

        if (parsedData.type == "PROJECT_FILE_ADDED") {
            this.eventHandlers.forEach((handler) => {
                handler.projectFileAddedEvent(parsedData);
            });
        }

        if (parsedData.type == "PROJECT_FILE_UPDATED") {
            this.eventHandlers.forEach((handler) => {
                handler.projectFileUpdatedEvent(parsedData);
            });
        }

        if (parsedData.type == "PROJECT_FILE_DELETED") {
            this.eventHandlers.forEach((handler) => {
                handler.projectFileDeletedEvent(parsedData);
            });
        }

        if (parsedData.type == "UNZIPPED_PROJECT_FILES_UPDATED") {
            this.eventHandlers.forEach((handler) => {
                handler.unzippedProjectFilesUpdatedEvent(parsedData);
            });
        }

        if (parsedData.type == "PROJECT_UPDATED") {
            this.eventHandlers.forEach((handler) => {
                handler.projectUpdatedEvent(parsedData);
            });
        }

        if (parsedData.type == "PROJECT_ADDED") {
            this.eventHandlers.forEach((handler) => {
                handler.projectAddedEvent(parsedData);
            });
        }

        if (parsedData.type == "JOB_STATUS_UPDATE") {
            this.eventHandlers.forEach((handler) => {
                handler.jobStatusUpdatedEvent(parsedData);
            });
        }
    }

    private buildWsUrl(sessionId?: string) {
        if (!sessionId) {
            return this.baseWsUrl;
        }

        try {
            const wsUrl = new URL(this.baseWsUrl);
            const isLocalProxy = wsUrl.hostname === "localhost" || wsUrl.hostname === "127.0.0.1";
            if (wsUrl.host === window.location.host && !isLocalProxy) {
                return this.baseWsUrl;
            }
            wsUrl.searchParams.set("session_id", sessionId);
            return wsUrl.toString();
        } catch {
            const separator = this.baseWsUrl.includes("?") ? "&" : "?";
            return `${this.baseWsUrl}${separator}session_id=${encodeURIComponent(sessionId)}`;
        }
    }
}
